// Import
import { Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  InputCst,
  SelectCst,
  AutocompleteCst,
  MultiSelectCst,
  RadioCst,
  DataPickerCst,
  SingleCheckboxCst,
  InputAssociationChipsCst,
  MultiTextCst,
} from "@components/InputCst";
import { ColumnInterface } from "@components/Table/types";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { defaultValidation } from "@utils/utilsValidators";
import ButtonCst from "@components/ButtonCst";
import DownloaderUploaderCst from "@components/DownloaderUploaderCst";
import { useNavigate } from "react-router-dom";
import InputChipsCst from "@components/InputCst/InpuctChipsCst";
import AssociationTableCst from "@components/AssociationTableCst";
import { PagesID } from "@utils/utilsConfigurations";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import { TextEditorCst } from "@components/TextEditorCst";
import { Association } from "src/interfaces/Common";
import SessionEditionFormCst from "@components/SessionEditionFormCst";
import dayjs from "dayjs";
import SessionAccordionCst from "@components/SessionAccordionCst";
import { SessionPostInterface } from "./types";

// Interface

interface EditionFormCstInterface {
  isVirtual?: boolean;
  isAdd?: boolean;
  row?: any;
  columns: ColumnInterface[];
  additionalColumns?: ColumnInterface[];
  actionsForForms?: ColumnInterface[];
  onSubmit: (inputForm: any, sessions: any) => void;
  onReset?: () => void;
  inputsException?: (col: any, inputForm: any) => any;
  backButton?: () => any;
  getValues?: (accessorKey: string) => any;
  handleDelete?: (accessorKey: string, element: any) => void;
  handleAssociation?: (accessorKey: string, value: any) => void;
  setOpenAssociation?: (accessorKey: string, open: boolean) => void;
  association?: boolean | Association;
  isFaculties?: boolean;
  disabledConfig?: { view: string; accessoryKey: string };
  readOnly?: boolean;
  isCorporate?: boolean;
  deleteDisabled?: boolean;
  options?: any;
  sessionColumns?: any;
  resetSessionAssociations: () => void;
  setSiteAssociation?: any;
  setRoomAssociation?: any;
  setFacultiesAssociation: any;
  setTutorsAssociation: any;
  siteAssociation?: any;
  facultiesAssociation?: any;
  roomAssociation?: any;
  tutorsAssociation?: any;
  producerAssociation?: any;
  presenterAssociation?: any;
  coorganizerAssociation?: any;
  cohostAssociation?: any;
  setProducerAssociation?: any;
  setPresenterAssociation?: any;
  setCoorganizerAssociation?: any;
  setCohostAssociation?: any;
  setActivityDuration?: (item: number) => void;
  getDisabled?: (accessorKey: string) => any;
  confirmedDisabledTooltip?: string;
}

// eslint-disable-next-line no-empty-pattern
const EditionFormCst: React.FC<EditionFormCstInterface> = ({
  isVirtual,
  isAdd = false,
  row,
  columns,
  actionsForForms = [],
  inputsException,
  onSubmit,
  onReset,
  backButton,
  getValues,
  handleDelete,
  handleAssociation,
  setOpenAssociation,
  association,
  readOnly,
  options,
  sessionColumns,
  resetSessionAssociations,
  setSiteAssociation,
  setRoomAssociation,
  setFacultiesAssociation,
  setTutorsAssociation,
  siteAssociation,
  facultiesAssociation,
  roomAssociation,
  tutorsAssociation,
  producerAssociation,
  presenterAssociation,
  coorganizerAssociation,
  cohostAssociation,
  setProducerAssociation,
  setPresenterAssociation,
  setCoorganizerAssociation,
  setCohostAssociation,
  setActivityDuration,
  getDisabled,
  confirmedDisabledTooltip,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["form"]);
  const navigate = useNavigate();
  const [inputForm, setInputForm] = useState<any>({});
  const [sessionForm, setSessionForm] = useState<any>({});
  const [errorInputForm, setErrorInputForm] = useState<any>({});
  const [fileUploaded, setFileUploaded] = useState<any>(null);
  const [sessions, setSessions] = useState<SessionPostInterface[]>([]);
  const [newSession, setNewSession] = useState<boolean>(false);
  const [mainFormCompleted, setMainFormCompleted] = useState<boolean>(false);
  const [editSessionObject, setEditSessionObject] = useState<any>({});
  const [editSessionIndex, setEditSessionIndex] = useState<any>(null);
  const [sessionsCountError, setSessionsCountError] = useState(false);
  const isPublishFuture = row.publish_date
    ? dayjs().isBefore(row.publish_date)
    : undefined;

  function mapParticipants(participants: any): any[] | null {
    if (!Array.isArray(participants)) return null;

    return participants.map((p) => ({
      ...p,
      label: p.label || `${p.name} ${p.lastname}`,
    }));
  }

  const getInitialData = () => {
    let initialState: any = {};

    columns?.forEach(({ accessorKey }) => {
      initialState = {
        ...initialState,
        [accessorKey]: row[accessorKey],
      };
    });

    //FORMATTO LA SESSION CON I DATI DELL ONLINE MEETING
    setSessions(formatSession(row?.sessions || []));

    return initialState;
  };

  const formatSession = (sessions: any) => {
    if (sessions && sessions.length > 0) {
      return sessions.map((session: any) => ({
        ...session,
        producer: mapParticipants(session?.producer),
        coorganizer: mapParticipants(
          session?.coorganizer || session?.co_organizer,
        ),
        presenter: mapParticipants(session?.presenter),
        cohost: mapParticipants(session?.cohost),
        session_confirmed: session?.confirmed,
      }));
    }

    return sessions;
  };

  const resetInputForm = () => {
    let initialState = {};
    onReset && onReset();

    if (row) {
      setInputForm(getInitialData());
    } else {
      setInputForm(initialState);
    }
  };

  useEffect(() => {
    if (
      row &&
      Object.keys(row).length > 0 &&
      columns?.length > 0 &&
      Object.keys(inputForm).length === 0
    ) {
      const initialState = getInitialData();
      setInputForm(initialState);

      // Disable all columns if readOnly=true prop is passed
      if (readOnly) {
        columns?.forEach((column) => (column.disabled = true));
      }
    }
  }, [row.sessions, columns]);

  useEffect(() => {
    if (!Object.keys(inputForm).length) return;
    const { canBeSubmit, showError } = defaultValidation(columns, inputForm);
    if (canBeSubmit) {
      setMainFormCompleted(true);
    }
  }, [inputForm]);

  const onConfirm = () => {
    const { canBeSubmit, showError } = defaultValidation(columns, inputForm);

    if (!sessions.length) {
      setSessionsCountError(true);
      return;
    }

    setErrorInputForm(showError);
    const corporateDomainExist = columns.find(
      (item) => item.accessorKey === "corporate_domain",
    );

    if (corporateDomainExist != undefined) {
      if (
        inputForm["corporate_domain"] === "" ||
        inputForm["corporate_domain"] === undefined
      ) {
        inputForm["corporate_domain"] = null;
      }
    }

    if (canBeSubmit) {
      onSubmit(inputForm, sessions);
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorInputForm[accessorKey]?.msg || errorInputForm[accessorKey];
  };

  const minLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const maxLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const getInputsList = (col: any, inputForm: any) => {
    if (inputsException) {
      col = inputsException(col, inputForm);
    }

    switch (col.type) {
      case "association": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={col.fullWidth ? 12 : 6}>
            <InputAssociationChipsCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={getValues && getValues(col.accessorKey)}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              noAssociationButton={col.disableAssociationButton}
              disabled={readOnly || col.disabled}
              textTooltip={col.tooltip}
              required={col.required}
              onDelete={(e: any) => {
                handleDelete && handleDelete(col.accessorKey, e);
              }}
              onRedirect={() => {
                setOpenAssociation && setOpenAssociation(col.accessorKey, true);
              }}
              labelKey={col.labelKey}
            />
          </Grid>
        );
      }
      case "singleCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SingleCheckboxCst
              key={col.accessorKey}
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              label={col.header}
              onChange={(e: any) => {
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.checked },
                });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "text": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <InputCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.value },
                });
              }}
              maxValue={col?.rules?.max?.value}
              multiline={col.multiline}
              rows={col.rows}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
              textTooltip={col.tooltip}
              type={col.inpuType || "text"}
            />
          </Grid>
        );
      }
      case "chips": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <InputChipsCst
              label={col.header}
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              required={col.required}
              maxLength={col?.rules?.max?.value}
              disabled={readOnly || col.disabled}
              textTooltip={col.textTooltip}
              validationType={col.validationType}
              error={errorInputForm[col.accessorKey] ? true : false}
              onAdd={(list: any[]) => {
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: list },
                });
              }}
              deleteDisabled={col.deleteDisabled}
              undeletableValues={col.undeletableValues}
            />
          </Grid>
        );
      }
      case "select": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              optionsToDisable={col.optionsToDisable}
              value={inputForm[col.accessorKey]} //REFACTOR temp to show right value on select
              service={col.service}
              required={col.required}
              onChange={(e: any) => {
                if (col.callBack) {
                  setInputForm(col.callBack(e, row));
                } else {
                  setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
                }
              }}
              options={col?.options}
              textTooltip={col.tooltip}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "select-checkbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              id={col.accessorKey}
              label={col.header}
              optionsKey={col.optionsKey}
              value={inputForm[col.accessorKey]}
              optionsToDisable={col.optionsToDisable}
              disabled={readOnly || col.disabled}
              onChange={(e: any) => {
                let newValue = JSON.parse(JSON.stringify(e));
                let newInputForm = JSON.parse(JSON.stringify(inputForm));
                setInputForm({
                  ...newInputForm,
                  ...{ [col.accessorKey]: newValue },
                });
              }}
              options={col.options}
              required={col.required}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "autocomplete": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <AutocompleteCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "multiCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              textTooltip={col.tooltip}
              required={col.required}
              service={col.service}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "radio": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <RadioCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "dateTime": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              minDate={col.minDate}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "dateTimeYear": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              views={["year"]}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={readOnly || col.disabled}
            />
          </Grid>
        );
      }
      case "time": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <TimePickerCst
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`) || "Error"
              }
              required={col.required}
              label={col.header}
              disabled={readOnly || col.disabled}
              onChange={(e: any) => {
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
            />
          </Grid>
        );
      }
      case "multi-text": {
        return (
          <MultiTextCst
            key={col.accessorKey}
            label={col.header}
            fields={col.fields}
            value={inputForm[col.accessorKey]}
            disabled={readOnly || col.disabled}
            onChange={(e: any) => {
              setInputForm({
                ...inputForm,
                ...{ [col.accessorKey]: e },
              });
            }}
          />
        );
      }
      case "editor": {
        return (
          <TextEditorCst
            options={col.editorOptions}
            value={inputForm[col.accessorKey]}
            onChange={(e: any) => {
              setInputForm((prev: any) => {
                return {
                  ...prev,
                  [col.accessorKey]: e,
                };
              });
            }}
          />
        );
      }
      case "upload": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DownloaderUploaderCst
              name={col.header}
              accept={col?.accept}
              url={col.service}
              type={col.upload_type}
              onChange={(e: any) => {
                setInputForm({
                  ...inputForm,
                  [col.accessorKey]: {
                    e,
                  },
                });
                setFileUploaded({
                  ...fileUploaded,
                  [col.accessorKey]: {
                    file: e,
                  },
                });
              }}
              fileName={!!row?.originalFileName && row.originalFileName}
              downloadDisabled={!!col.downloadDisabled}
              onDownload={col.downloadCallback && col.downloadCallback}
              onTemplateDownload={col.templateCallback && col.templateCallback}
              onDelete={
                col.deleteCallback &&
                (() => {
                  setFileUploaded(null);
                  return col.deleteCallback && col.deleteCallback();
                })
              }
              disabled={readOnly || col.disabled}
              required={col?.required}
              showError={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      default:
        return <></>;
    }
  };

  const getInputField = useMemo(() => {
    return columns?.map((col: any) => {
      if (!!col.knowledge) {
        return false;
      }
      return getInputsList(col, inputForm);
    });
  }, [
    columns,
    inputForm,
    errorInputForm,
    t,
    getErrMsg,
    getValues,
    handleDelete,
    maxLength,
    minLength,
    setOpenAssociation,
  ]);

  const resetNewSession = () => {
    setNewSession(false);
    setSessionForm({});
    resetSessionAssociations();
  };

  const resetEditSession = () => {
    setEditSessionIndex(null);
    setEditSessionObject({});
    setSessionForm({});
    resetSessionAssociations();
  };

  const saveSession = (savedSessionForm: SessionPostInterface) => {
    let sessionCreated: any;

    if (isVirtual) {
      sessionCreated = {
        ...savedSessionForm,
        faculties: facultiesAssociation,
        tutors: tutorsAssociation,
        start_date: dayjs(savedSessionForm.start_date).format(
          "YYYY-MM-DDTHH:mm:ss.SSSZ",
        ),
        end_date: dayjs(savedSessionForm.end_date).format(
          "YYYY-MM-DDTHH:mm:ss.SSSZ",
        ),
        publish_date_session: inputForm.publish_date,
        tool: savedSessionForm.tool || null,
        producer: producerAssociation,
        presenter: presenterAssociation,
        coorganizer: coorganizerAssociation,
        cohost: cohostAssociation,
      };
    } else {
      sessionCreated = {
        ...savedSessionForm,
        faculties: facultiesAssociation,
        rooms: roomAssociation,
        site: siteAssociation,
        tutors: tutorsAssociation,
        start_date: dayjs(savedSessionForm.start_date).format(
          "YYYY-MM-DDTHH:mm:ss.SSSZ",
        ),
        end_date: dayjs(savedSessionForm.end_date).format(
          "YYYY-MM-DDTHH:mm:ss.SSSZ",
        ),
        publish_date_session: inputForm.publish_date,
      };
    }

    if (!!Object.keys(editSessionObject).length) {
      const updatedSessions = [...sessions];
      updatedSessions[editSessionIndex - 1] = sessionCreated;
      setSessions(updatedSessions);
    } else {
      setSessions([...sessions, sessionCreated]);
    }

    resetNewSession();
    setEditSessionIndex(null);
    setEditSessionObject({});
  };

  const deleteSingleSession = (session: SessionPostInterface) => {
    const newSessionsList = sessions.filter((item) => item !== session);
    setSessions(newSessionsList);
  };

  const editSingleSession = (session: SessionPostInterface, index: number) => {
    setNewSession(false);
    setSessionForm({});
    setEditSessionObject({});
    setEditSessionObject(session);
    session.site && setSiteAssociation(session.site);
    session.rooms && setRoomAssociation(session.rooms);
    setFacultiesAssociation(session.faculties || []);
    setTutorsAssociation(session.tutors || []);
    session.producer && setProducerAssociation(session.producer);
    session.presenter && setPresenterAssociation(session.presenter);
    session.coorganizer && setCoorganizerAssociation(session.coorganizer);
    session.cohost && setCohostAssociation(session.cohost);
    setEditSessionIndex(index + 1);
  };

  const orderSessionsByDates = () => {
    const orderedArray = sessions.sort((a, b) => {
      return (
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
    });
    return orderedArray;
  };

  return (
    <>
      {!association && (
        <Grid container>
          <Grid item xs={0} lg={4} xl={3}>
            <BackgroundImageCst
              image="section-internal-form.jpg"
              disableGradient
            />
          </Grid>
          <Grid item xs={12} lg={8} xl={9}>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                padding: "32px",
                minHeight: "495px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Grid container spacing={3}>
                {getInputField}
              </Grid>
              {!!sessions &&
                !!sessions.length &&
                orderSessionsByDates().map((session: any, index: number) => (
                  <>
                    {!!Object.keys(editSessionObject).length &&
                    editSessionIndex - 1 === index ? (
                      <SessionEditionFormCst
                        isAdd={isAdd}
                        mainForm={inputForm}
                        sessions={sessions}
                        columns={sessionColumns}
                        row={editSessionObject}
                        editIndex={editSessionIndex}
                        onSubmit={saveSession}
                        onResetEdit={resetEditSession}
                        sessionsCount={sessions.length + 1}
                        setSessionForm={setSessionForm}
                        sessionForm={sessionForm}
                        setOpenAssociation={setOpenAssociation}
                        handleDelete={handleDelete}
                        handleAssociation={handleAssociation}
                        getValues={getValues}
                        association={association}
                        siteAssociation={siteAssociation}
                        facultiesAssociation={facultiesAssociation}
                        tutorsAssociation={tutorsAssociation}
                        roomAssociation={roomAssociation}
                        producerAssociation={producerAssociation}
                        presenterAssociation={presenterAssociation}
                        coorganizerAssociation={coorganizerAssociation}
                        cohostAssociation={cohostAssociation}
                        isPublishFuture={isPublishFuture}
                        setSiteAssociation={setSiteAssociation}
                        setRoomAssociation={setRoomAssociation}
                        isVirtual={isVirtual || false}
                        confirmedDisabledTooltip={confirmedDisabledTooltip}
                      />
                    ) : (
                      <SessionAccordionCst
                        isVirtual={isVirtual}
                        data={session}
                        index={index}
                        onEdit={editSingleSession}
                        onDelete={deleteSingleSession}
                        disabled={readOnly}
                      />
                    )}
                  </>
                ))}
              {newSession && (
                <SessionEditionFormCst
                  isNew={true}
                  isAdd={isAdd}
                  mainForm={inputForm}
                  sessions={sessions}
                  columns={sessionColumns}
                  row={editSessionObject}
                  editIndex={editSessionIndex}
                  onSubmit={saveSession}
                  onReset={resetNewSession}
                  sessionsCount={sessions ? sessions.length + 1 : 1}
                  setSessionForm={setSessionForm}
                  sessionForm={sessionForm}
                  setOpenAssociation={setOpenAssociation}
                  handleDelete={handleDelete}
                  handleAssociation={handleAssociation}
                  getValues={getValues}
                  association={association}
                  siteAssociation={siteAssociation}
                  facultiesAssociation={facultiesAssociation}
                  tutorsAssociation={tutorsAssociation}
                  roomAssociation={roomAssociation}
                  producerAssociation={producerAssociation}
                  presenterAssociation={presenterAssociation}
                  coorganizerAssociation={coorganizerAssociation}
                  cohostAssociation={cohostAssociation}
                  isPublishFuture={isPublishFuture}
                  setSiteAssociation={setSiteAssociation}
                  setRoomAssociation={setRoomAssociation}
                  isVirtual={isVirtual || false}
                  confirmedDisabledTooltip={confirmedDisabledTooltip}
                />
              )}
              {(!row?.publish_date || isPublishFuture) && (
                <Box
                  sx={{
                    mt: "2rem",
                    mb: "4rem",
                  }}
                >
                  <ButtonCst
                    id={"back-button"}
                    variant={"contained"}
                    size={"medium"}
                    sx={{
                      minWidth: "150px",
                      [theme.breakpoints.down("lg")]: {
                        minWidth: "100px",
                      },
                      [theme.breakpoints.down("sm")]: {
                        minWidth: "100%",
                      },
                    }}
                    onClick={() => setNewSession(true)}
                    disabled={
                      !mainFormCompleted ||
                      newSession ||
                      !!Object.keys(editSessionObject).length
                    }
                  >
                    {t("session-form.add-session")}
                  </ButtonCst>
                  {sessions && !sessions.length && sessionsCountError && (
                    <Typography color={theme.palette.error.main}>
                      {t("session-form.no-session-added")}
                    </Typography>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  mt: "32px",
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  justifyContent: "space-between",
                  [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                  },
                }}
              >
                <ButtonCst
                  id={"back-button"}
                  variant={"outlined"}
                  size={"medium"}
                  sx={{
                    minWidth: "150px",
                    mr: "auto",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={() => {
                    if (backButton) {
                      backButton();
                    } else {
                      navigate(-1);
                    }
                  }}
                >
                  {t("backButton")}
                </ButtonCst>
                <ButtonCst
                  id={"reset-button"}
                  variant={"outlined"}
                  size={"medium"}
                  disabled={readOnly}
                  sx={{
                    minWidth: "150px",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={resetInputForm}
                >
                  {t("resetButton")}
                </ButtonCst>
                <ButtonCst
                  id={"save-button"}
                  variant={"contained"}
                  size={"medium"}
                  sx={{
                    minWidth: "150px",
                    [theme.breakpoints.down("lg")]: {
                      minWidth: "100px",
                    },
                    [theme.breakpoints.down("sm")]: {
                      minWidth: "100%",
                    },
                  }}
                  onClick={() => onConfirm()}
                  disabled={
                    !!readOnly ||
                    newSession ||
                    Object.keys(editSessionObject).length > 0
                  }
                >
                  {t("saveButton")}
                </ButtonCst>
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
      {(association as { type: string })?.type === "table" && (
        <AssociationTableCst
          association={
            getValues && getValues((association as Association).accessorKey)
          }
          associationDisabled={
            getDisabled && getDisabled((association as Association).accessorKey)
          }
          setAssociation={null}
          callBackAssociation={(value: any, output: any) => {
            const objToValue: any = Object.keys(value).map((key: string) => {
              if (key.includes("||")) {
                return {
                  id: key.split("||")[0],
                  label: key.split("||")[1],
                };
              } else {
                return value.find((elem: any) => elem.id === +key);
              }
            });

            handleAssociation &&
              handleAssociation(
                (association as Association).accessorKey,
                value,
              );
            const sessionAssociationFields = ["site", "rooms", "faculties"];
            const isSessionForm = sessionAssociationFields.includes(
              [(association as Association).accessorKey][0],
            );

            if (isSessionForm) {
              setSessionForm({
                ...sessionForm,
                ...{ [(association as Association).accessorKey]: objToValue },
              });
            } else {
              setInputForm({
                ...inputForm,
                ...{ [(association as Association).accessorKey]: value },
              });
            }

            if (
              (association as Association).accessorKey === "learning_objects"
            ) {
              const idSet: number[] = objToValue.map(
                (item: { id: string | number; label: string }) =>
                  Number(item.id),
              );
              const fullList = output.filter((item: any) =>
                idSet.includes(item.id),
              );
              const totalDuration = fullList.reduce(
                (sum: any, item: any) => sum + (item.duration || 0),
                0,
              );

              setActivityDuration && setActivityDuration(totalDuration);
            }

            setOpenAssociation &&
              setOpenAssociation(
                (association as Association).accessorKey,
                false,
              );
          }}
          setCloseAssociation={(value: boolean) =>
            setOpenAssociation &&
            setOpenAssociation((association as Association).accessorKey, value)
          }
          title={t((association as Association).titleKey)}
          service={(association as { service: string }).service}
          pageID={(association as { pageId: PagesID }).pageId}
          filter={(association as Association).accessorKey}
          defaultFilters={(association as Association).defaultFilters}
          manageConfigs={(association as Association).manageConfigs}
          enableMultiRowSelection={(association as Association).multiChoice}
          labelKey={(association as Association).labelKey}
          removeCTAfromRowOrTable={["add", "template", "download", "upload"]}
        />
      )}
    </>
  );
};

export default EditionFormCst;
